import type { ProjectDefinition } from "../../../types";
import { type CellValue, type FuncionProjectSpec, conditions, seededRandom } from "../spec";

const SHEET = "AV Sucursales";
const TABLE = "SucursalesAV";
const SUCURSALES = [
  "Centro", "Norte", "Sur", "Poniente", "Oriente", "Aeropuerto", "Galerías", "Valle",
  "Lomas", "Satélite", "Coyoacán", "Polanco", "Tlalpan", "Santa Fe", "Interlomas",
] as const;
const REGIONES = ["Metro Norte", "Metro Sur", "Metro Centro"] as const;

const rnd = seededRandom(3636);
// Sucursal, Región, Ventas, Meta — distinct values so sorting has no ties.
const DATA = SUCURSALES.map((s, i) => [
  `Sucursal ${s}`,
  REGIONES[i % REGIONES.length],
  900000 + i * 17310 + rnd.int(0, 60) * 10000,
  1000000 + rnd.int(0, 50) * 10000 + i * 130,
] as [string, string, number, number]);
// Exported reports usually end with a totals row: EXCLUIR(…,-1) removes it.
const TOTAL: [string, string, number, number] = [
  "TOTAL",
  "Todas",
  DATA.reduce((s, r) => s + r[2], 0),
  DATA.reduce((s, r) => s + r[3], 0),
];
const ROWS = [...DATA, TOTAL];

const IN = { n: 3 };
const porVentas = [...DATA].sort((a, b) => b[2] - a[2]);

const EXPECTED: CellValue[][][] = [
  ROWS.slice(0, 5),
  ROWS.slice(-3),
  DATA,
  porVentas.slice(0, 5),
  porVentas.slice(0, IN.n),
  DATA.map((r) => [r[0], r[1]]),
  [...DATA].sort((a, b) => a[2] / a[3] - b[2] / b[3]).slice(0, 3),
];
const FNS = [
  ["TAKE"], ["TAKE"], ["DROP"], ["TAKE", "SORT", "DROP"], ["TAKE", "SORT", "DROP"], ["TAKE", "DROP"],
  ["LET", "TAKE", "SORTBY", "DROP"],
];
const LABELS = [
  "Tarea 1 · Primeras 5 filas",
  "Tarea 2 · Últimas 3 filas",
  "Tarea 3 · Sin la fila de total",
  "Tarea 4 · Top 5 por ventas",
  "Tarea 5 · Top N",
  "Tarea 6 · Solo sucursal y región",
  "Tarea 7 · Las 3 con menor cumplimiento",
];

const ANCHORS: string[] = [];
EXPECTED.reduce((row, result) => {
  ANCHORS.push(`H${row}`);
  return row + result.length + 3;
}, 8);

export const spec: FuncionProjectSpec = {
  projectId: "funciones-tomar",
  sheets: [
    {
      name: SHEET,
      title: "Top N y recortes",
      subtitle: "Reporte de sucursales exportado del sistema — con su fila de TOTAL al final",
      tables: [{ name: TABLE, anchor: "A4", headers: ["Sucursal", "Región", "Ventas", "Meta"], rows: ROWS, formats: { Ventas: "$#,##0", Meta: "$#,##0" } }],
      inputs: [{ address: "H4", label: "¿Cuántas sucursales (N)?", value: IN.n }],
      answers: ANCHORS.map((address, i) => ({ address, label: LABELS[i], labelPosition: "above" as const })),
    },
  ],
  checks: Object.fromEntries(
    EXPECTED.map((expected, i) => [
      i + 1,
      { kind: "spill" as const, sheet: SHEET, address: ANCHORS[i], fns: FNS[i], expected },
    ])
  ),
};

const F = (text: string) => `<span class="formula">${text}</span>`;
const LET_FORMULA = "=LET(d,EXCLUIR(SucursalesAV,-1),TOMAR(ORDENARPOR(d,ELEGIRCOLS(d,3)/ELEGIRCOLS(d,4),1),3))";

export const project: ProjectDefinition = {
  id: "funciones-tomar",
  title: "TOMAR y EXCLUIR: Top N y recortes",
  subtitle: "Quedarte con el principio o el final, o quitarlos",
  description:
    "El sistema exporta el reporte de 15 sucursales con una fila TOTAL al final, que estorba al ordenar o filtrar. Dirección quiere el Top 5, un Top N que cambie al escribir otro número y las 3 sucursales con peor cumplimiento.\n\nTOMAR se queda con las primeras (o, con negativos, las últimas) filas o columnas. EXCLUIR hace lo contrario: las quita. Combinadas con ORDENAR se vuelven un generador de rankings. Al final conocerás LET, para ponerle nombre a un resultado y no repetirlo.",
  topics: [
    "TOMAR filas del principio y del final",
    "EXCLUIR la fila de totales",
    "Top N con ORDENAR + TOMAR",
    "N variable desde una celda",
    "TOMAR columnas",
    "LET: nombrar un resultado intermedio",
  ],
  tasks: [
    {
      id: 1,
      level: "basico",
      title: "Tarea 1: Primeras 5 filas",
      description: `En la hoja <strong>AV Sucursales</strong>, celda <strong>${ANCHORS[0]}</strong>: las <strong>5 primeras filas</strong> de SucursalesAV.`,
      solution: [`Selecciona ${F(ANCHORS[0])}.`, `Escribe ${F("=TOMAR(SucursalesAV,5)")}.`],
      expectedResult: "5 filas",
      finalFormula: "=TOMAR(SucursalesAV,5)",
    },
    {
      id: 2,
      level: "basico",
      title: "Tarea 2: Últimas 3 filas",
      description: `En <strong>${ANCHORS[1]}</strong>: las <strong>3 últimas filas</strong> (incluye la de TOTAL).`,
      solution: [
        `Selecciona ${F(ANCHORS[1])}.`,
        "Con un número negativo, TOMAR cuenta desde el final.",
        `Escribe ${F("=TOMAR(SucursalesAV,-3)")}.`,
      ],
      expectedResult: "Las 2 últimas sucursales y el TOTAL",
      finalFormula: "=TOMAR(SucursalesAV,-3)",
    },
    {
      id: 3,
      level: "intermedio",
      title: "Tarea 3: Quitar la fila de total",
      description: `En <strong>${ANCHORS[2]}</strong>: toda la tabla <strong>sin</strong> la fila TOTAL.`,
      solution: [`Selecciona ${F(ANCHORS[2])}.`, `Escribe ${F("=EXCLUIR(SucursalesAV,-1)")}.`],
      expectedResult: "15 sucursales",
      finalFormula: "=EXCLUIR(SucursalesAV,-1)",
    },
    {
      id: 4,
      level: "intermedio",
      title: "Tarea 4: Top 5 por ventas",
      description: conditions(`En <strong>${ANCHORS[3]}</strong>: las <strong>5 sucursales con más ventas</strong>:`, [
        "Quita el TOTAL (si no, siempre quedaría en el lugar 1)",
        "Ordena por Ventas (columna 3) de mayor a menor",
        "Toma las 5 primeras",
      ]),
      solution: [`Selecciona ${F(ANCHORS[3])}.`, `Escribe ${F("=TOMAR(ORDENAR(EXCLUIR(SucursalesAV,-1),3,-1),5)")}.`],
      expectedResult: "Top 5",
      finalFormula: "=TOMAR(ORDENAR(EXCLUIR(SucursalesAV,-1),3,-1),5)",
    },
    {
      id: 5,
      level: "intermedio",
      title: "Tarea 5: Top N variable",
      description: `En <strong>${ANCHORS[4]}</strong>: igual que la Tarea 4, pero la cantidad de sucursales la decide la celda <strong>H4</strong>.`,
      solution: [
        `Selecciona ${F(ANCHORS[4])}.`,
        `Escribe ${F("=TOMAR(ORDENAR(EXCLUIR(SucursalesAV,-1),3,-1),H4)")}.`,
        "Cambia H4 a 7 y mira cómo crece el resultado.",
      ],
      expectedResult: "Top 3 (con H4 = 3)",
      finalFormula: "=TOMAR(ORDENAR(EXCLUIR(SucursalesAV,-1),3,-1),H4)",
    },
    {
      id: 6,
      level: "reto",
      title: "Tarea 6: Solo las 2 primeras columnas",
      description: `En <strong>${ANCHORS[5]}</strong>: <strong>Sucursal</strong> y <strong>Región</strong> de todas las sucursales, sin la fila TOTAL.`,
      solution: [
        `Selecciona ${F(ANCHORS[5])}.`,
        "El tercer argumento de TOMAR son las columnas. Deja vacío el de filas para tomarlas todas.",
        `Escribe ${F("=TOMAR(EXCLUIR(SucursalesAV,-1),,2)")}.`,
      ],
      expectedResult: "15 filas × 2 columnas",
      finalFormula: "=TOMAR(EXCLUIR(SucursalesAV,-1),,2)",
    },
    {
      id: 7,
      level: "reto",
      title: "Tarea 7: Las 3 con menor cumplimiento",
      description: conditions(
        `En <strong>${ANCHORS[6]}</strong>: las <strong>3 sucursales con menor % de cumplimiento</strong> (Ventas ÷ Meta). Usa <strong>LET</strong> para no repetir EXCLUIR tres veces:`,
        [
          "<strong>d</strong> = la tabla sin el TOTAL",
          "Ordena <strong>d</strong> con ORDENARPOR usando Ventas ÷ Meta, de menor a mayor",
          "Toma las 3 primeras",
        ]
      ),
      solution: [
        `Selecciona ${F(ANCHORS[6])}.`,
        "LET(nombre, valor, cálculo) guarda un resultado con un nombre y luego lo usa las veces que quieras. La fórmula se lee mejor y Excel calcula EXCLUIR una sola vez.",
        `Escribe ${F(LET_FORMULA)}.`,
      ],
      expectedResult: "Las 3 sucursales más lejos de su meta",
      finalFormula: LET_FORMULA,
    },
  ],
};
